import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface UserInfo {
  dept_id: number | null
  username: string
  nickname: string
  email: string | null
  phone: string | null
  id: number
  user_id?: number
  avatar: string | null
  status: number
  is_superuser: boolean
  is_staff: boolean
  join_time: string
  last_login_time: string
  template_id: number | null
  roles: any[]
  permissions: any[]
}

export interface CurrentTeamCorp {
  corp_id: number
  name: string
}
export interface CurrentTeamInfo {
  corp_code: string
  corp_id: string
  corp: CurrentTeamCorp | null
  name: string
  is_admin: number
  is_vip: number
  create_user_name: string
  employee_count: number
}

export interface ClientInit {
  website_update: string | null
  is_inner: number | null
  jsApiList: string[]
  current_team: CurrentTeamInfo | null
}

export interface AuthResponse {
  access_token: string
  access_token_type?: string
  access_token_expire_time?: string
  refresh_token?: string
  refresh_token_type?: string
  refresh_token_expire_time?: string
  user?: UserInfo
  user_id?: number
  username?: string
  nickname?: string
  email?: string | null
  avatar?: string | null
  phone?: string | null
  status?: number
  is_staff?: boolean
  is_superuser?: boolean
  dept_id?: number | null
  join_time?: string
  last_login_time?: string
  template_id?: number
  roles?: any[]
  permissions?: any[]
}

export const useUserStore = defineStore('user', () => {
  const accessToken = ref<string | null>(null)
  const accessTokenType = ref<string | null>(null)
  const accessTokenExpireTime = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const refreshTokenType = ref<string | null>(null)
  const refreshTokenExpireTime = ref<string | null>(null)
  const user = ref<UserInfo | null>(null)
  const clientInit = ref<ClientInit | null>(null)

  // 团队相关（仅保存在内存，不做本地存储）
  const currentTeamInfo = ref<Record<string, any> | null>(null)
  const teamList = ref<Record<string, any>[]>([])

  const setCurrentTeamInfo = (data: Record<string, any> | null) => {
    currentTeamInfo.value = data
  }

  const setTeamList = (list: Record<string, any>[]) => {
    teamList.value = Array.isArray(list) ? list : []
  }

  const isTokenExpired = (iso: string | null) => {
    if (!iso) return false
    const t = Date.parse(iso)
    if (Number.isNaN(t)) return false
    return Date.now() >= t
  }

  const isLoggedIn = computed(() => {
    return !!accessToken.value && !isTokenExpired(accessTokenExpireTime.value)
  })

  const setAuthData = (data: AuthResponse) => {
    accessToken.value = data.access_token || null
    accessTokenType.value = data.access_token_type || 'Bearer'
    accessTokenExpireTime.value = data.access_token_expire_time || null
    refreshToken.value = data.refresh_token || null
    refreshTokenType.value = data.refresh_token_type || null
    refreshTokenExpireTime.value = data.refresh_token_expire_time || null

    const info: UserInfo = data.user
      ? {
        dept_id: data.user.dept_id ?? null,
        username: data.user.username ?? '',
        nickname: data.user.nickname ?? '',
        email: data.user.email ?? null,
        phone: data.user.phone ?? null,
        id: data.user.id ?? (data.user as any).user_id ?? 0,
        user_id: (data.user as any).user_id ?? undefined,
        avatar: data.user.avatar ?? null,
        status: data.user.status ?? 0,
        is_superuser: !!data.user.is_superuser,
        is_staff: !!data.user.is_staff,
        join_time: data.user.join_time ?? '',
        last_login_time: data.user.last_login_time ?? '',
        template_id: (data.user as any).template_id ?? null,
        roles: (data.user as any).roles ?? [],
        permissions: (data.user as any).permissions ?? []
      }
      : {
        dept_id: data.dept_id ?? null,
        username: data.username ?? '',
        nickname: data.nickname ?? '',
        email: data.email ?? null,
        phone: data.phone ?? null,
        id: (data.user_id as number) ?? 0,
        user_id: data.user_id ?? undefined,
        avatar: data.avatar ?? null,
        status: data.status ?? 0,
        is_superuser: !!data.is_superuser,
        is_staff: !!data.is_staff,
        join_time: data.join_time ?? '',
        last_login_time: data.last_login_time ?? '',
        template_id: data.template_id ?? null,
        roles: data.roles ?? [],
        permissions: data.permissions ?? []
      }

    if (typeof info.id !== 'number' && typeof info.user_id === 'number') {
      ; (info as any).id = info.user_id as number
    }

    user.value = info

    localStorage.setItem('ACCESS_TOKEN', accessToken.value || '')
    localStorage.setItem('ACCESS_TOKEN_TYPE', accessTokenType.value || 'Bearer')
    if (accessTokenExpireTime.value) {
      localStorage.setItem('ACCESS_TOKEN_EXPIRE_TIME', accessTokenExpireTime.value)
    } else {
      localStorage.removeItem('ACCESS_TOKEN_EXPIRE_TIME')
    }
    if (refreshToken.value) {
      localStorage.setItem('REFRESH_TOKEN', refreshToken.value)
    } else {
      localStorage.removeItem('REFRESH_TOKEN')
    }
    if (refreshTokenType.value) {
      localStorage.setItem('REFRESH_TOKEN_TYPE', refreshTokenType.value)
    } else {
      localStorage.removeItem('REFRESH_TOKEN_TYPE')
    }
    if (refreshTokenExpireTime.value) {
      localStorage.setItem('REFRESH_TOKEN_EXPIRE_TIME', refreshTokenExpireTime.value)
    } else {
      localStorage.removeItem('REFRESH_TOKEN_EXPIRE_TIME')
    }
    localStorage.setItem('USER_INFO', JSON.stringify(info))
  }

  const loadFromStorage = () => {
    accessToken.value = localStorage.getItem('ACCESS_TOKEN')
    accessTokenType.value = localStorage.getItem('ACCESS_TOKEN_TYPE')
    accessTokenExpireTime.value = localStorage.getItem('ACCESS_TOKEN_EXPIRE_TIME')
    refreshToken.value = localStorage.getItem('REFRESH_TOKEN')
    refreshTokenType.value = localStorage.getItem('REFRESH_TOKEN_TYPE')
    refreshTokenExpireTime.value = localStorage.getItem('REFRESH_TOKEN_EXPIRE_TIME')
    const u = localStorage.getItem('USER_INFO')
    user.value = u ? JSON.parse(u) : null
    const ci = localStorage.getItem('CLIENT_INIT')
    clientInit.value = ci ? JSON.parse(ci) : null
  }

  const clearAuth = () => {
    accessToken.value = null
    accessTokenType.value = null
    accessTokenExpireTime.value = null
    refreshToken.value = null
    refreshTokenType.value = null
    refreshTokenExpireTime.value = null
    user.value = null

    localStorage.removeItem('ACCESS_TOKEN')
    localStorage.removeItem('ACCESS_TOKEN_TYPE')
    localStorage.removeItem('ACCESS_TOKEN_EXPIRE_TIME')
    localStorage.removeItem('REFRESH_TOKEN')
    localStorage.removeItem('REFRESH_TOKEN_TYPE')
    localStorage.removeItem('REFRESH_TOKEN_EXPIRE_TIME')
    localStorage.removeItem('USER_INFO')
    localStorage.removeItem('CLIENT_INIT')
  }

  const getAuthHeader = computed(() => {
    const type = accessTokenType.value || 'Bearer'
    const token = accessToken.value || ''
    return token ? `${type} ${token}` : ''
  })

  const setClientInitData = (payload: any) => {
    const data = payload && payload.data && payload.data.data ? payload.data.data : (payload.data || payload)
    const ci: ClientInit = {
      website_update: data?.website_update ?? null,
      is_inner: data?.is_inner ?? null,
      jsApiList: Array.isArray(data?.jsApiList) ? data.jsApiList : [],
      current_team: data?.current_team ?? null
    }
    clientInit.value = ci
    localStorage.setItem('CLIENT_INIT', JSON.stringify(ci))
  }

  loadFromStorage()

  return {
    accessToken,
    accessTokenType,
    accessTokenExpireTime,
    refreshToken,
    refreshTokenType,
    refreshTokenExpireTime,
    user,
    isLoggedIn,
    getAuthHeader,
    setAuthData,
    loadFromStorage,
    clearAuth,
    clientInit,
    setClientInitData,
    currentTeamInfo,
    teamList,
    setCurrentTeamInfo,
    setTeamList
  }
})
